import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ZapIcon, ClockIcon, TargetIcon, TrophyIcon } from 'lucide-react';
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const features = [{
    icon: ZapIcon,
    title: 'Tensión Estructural',
    description: 'El mecanismo único que reprograma tu metabolismo: carga progresiva científicamente calibrada.'
  }, {
    icon: ClockIcon,
    title: '90 Minutos Semanales',
    description: '3 sesiones de 30 minutos. Diseñado para el argentino que trabaja mucho y tiene poco tiempo.'
  }, {
    icon: TargetIcon,
    title: 'Protocolo Específico',
    description: 'No es fitness genérico. Es ingeniería humana aplicada al contexto argentino +35.'
  }, {
    icon: TrophyIcon,
    title: 'Resultados Medibles',
    description: 'Fuerza, densidad ósea, metabolismo. Cambios que se sienten en los huesos, literalmente.'
  }];
  return <section ref={ref} className="w-full bg-bg-primary py-20 px-4 sm:px-6 lg:px-8" data-id="element-159">
      <div className="max-w-6xl mx-auto" data-id="element-160">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-161">
          <div className="inline-block px-4 py-2 bg-accent-bronze/20 border border-accent-bronze/30 rounded-sm mb-6" data-id="element-162">
            <span className="text-accent-bronze font-heading text-lg" data-id="element-163">
              La Solución
            </span>
          </div>

          <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-text-primary mb-6" data-id="element-164">
            Código Longevidad:{' '}
            <span className="text-accent-bronze" data-id="element-165">Fuerza Progresiva</span>
          </h2>

          <p className="text-xl sm:text-2xl text-text-secondary max-w-3xl mx-auto font-body leading-relaxed" data-id="element-166">
            No es un libro de fitness para instagramers. Es un manual de
            ingeniería humana diseñado para el contexto argentino: para el que
            trabaja mucho, tiene poco tiempo y quiere resultados que se sientan
            en los huesos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-167">
          {features.map((feature, index) => {
          const Icon = feature.icon;
          return <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.15
          }} className="bg-bg-secondary border border-accent-bronze/20 rounded-sm p-8 hover:border-accent-bronze/40 transition-colors duration-300" data-id="element-168">
                <div className="flex items-start gap-4" data-id="element-169">
                  <div className="flex-shrink-0 w-14 h-14 bg-accent-bronze/10 rounded-sm flex items-center justify-center" data-id="element-170">
                    <Icon className="w-7 h-7 text-accent-bronze" data-id="element-171" />
                  </div>
                  <div data-id="element-172">
                    <h3 className="font-heading text-2xl text-text-primary mb-2" data-id="element-173">
                      {feature.title}
                    </h3>
                    <p className="text-text-secondary font-body leading-relaxed" data-id="element-174">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="bg-gradient-to-br from-accent-bronze/10 via-bg-secondary to-bg-secondary border border-accent-bronze/30 rounded-sm p-8 sm:p-12" data-id="element-175">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-176">
            <div data-id="element-177">
              <h3 className="font-heading text-4xl sm:text-5xl text-text-primary mb-6" data-id="element-178">
                El Protocolo de Tensión Estructural
              </h3>
              <p className="text-lg text-text-secondary font-body leading-relaxed mb-4" data-id="element-179">
                Aplicamos la dosis mínima de fuerza para reprogramar tu
                metabolismo. No se trata de volumen, se trata de intensidad
                calibrada.
              </p>
              <p className="text-lg text-text-secondary font-body leading-relaxed" data-id="element-180">
                Cada ejercicio, cada serie, cada descanso está diseñado para
                maximizar la respuesta anabólica sin destruir tu recuperación.
                Ciencia aplicada, no gimnasia recreativa.
              </p>
            </div>

            <div className="space-y-4" data-id="element-181">
              <div className="bg-bg-tertiary border border-accent-bronze/20 rounded-sm p-6" data-id="element-182">
                <div className="flex items-center justify-between mb-2" data-id="element-183">
                  <span className="font-heading text-xl text-text-primary" data-id="element-184">
                    Frecuencia
                  </span>
                  <span className="text-accent-bronze font-heading text-2xl" data-id="element-185">
                    3x/semana
                  </span>
                </div>
                <p className="text-text-muted text-sm font-body" data-id="element-186">
                  Lunes, Miércoles, Viernes
                </p>
              </div>

              <div className="bg-bg-tertiary border border-accent-bronze/20 rounded-sm p-6" data-id="element-187">
                <div className="flex items-center justify-between mb-2" data-id="element-188">
                  <span className="font-heading text-xl text-text-primary" data-id="element-189">
                    Duración
                  </span>
                  <span className="text-accent-bronze font-heading text-2xl" data-id="element-190">
                    30 min
                  </span>
                </div>
                <p className="text-text-muted text-sm font-body" data-id="element-191">
                  Por sesión, sin relleno
                </p>
              </div>

              <div className="bg-bg-tertiary border border-accent-bronze/20 rounded-sm p-6" data-id="element-192">
                <div className="flex items-center justify-between mb-2" data-id="element-193">
                  <span className="font-heading text-xl text-text-primary" data-id="element-194">
                    Programa
                  </span>
                  <span className="text-accent-bronze font-heading text-2xl" data-id="element-195">
                    12 semanas
                  </span>
                </div>
                <p className="text-text-muted text-sm font-body" data-id="element-196">
                  Progresión estructurada
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}